/**
 * WCAG 1.4.13 (Content on Hover or Focus) for the product color-swatch tooltips.
 *
 * Each swatch shows its color name in a ".color-swatch__tooltip" that the theme
 * reveals on CSS :hover of the surrounding ".color-swatch-wrap" and (via the
 * accompanying SCSS) on keyboard :focus of the swatch radio input. Two of the
 * three 1.4.13 requirements are handled in CSS:
 *   - hoverable: the tooltip is nested inside the hovered wrap and a transparent
 *     ::before bridges the margin gap, so moving the pointer onto it keeps the
 *     wrap :hover alive;
 *   - persistent: it stays for as long as the swatch is hovered or focused.
 *
 * The missing piece is "dismissible without moving the pointer or focus" — Escape
 * cannot undo a CSS :hover/:focus. This script adds it: pressing Escape while a
 * swatch is hovered or focused hides its tooltip (via the .tooltip-dismissed
 * class, whose CSS wins over the reveal rules) until the pointer leaves and
 * re-enters, or focus leaves and returns to, that swatch.
 *
 * Layered on top of the theme; touches no third-party code.
 */
(function () {
    'use strict';

    var WRAP_SELECTOR = '.color-swatch-wrap';
    var INPUT_SELECTOR = '.variant__input--color-swatch';
    var DISMISSED_CLASS = 'tooltip-dismissed';

    // The .color-swatch-wrap that follows a given focused swatch radio, if any.
    function wrapForInput(input) {
        if (!input || !input.matches || !input.matches(INPUT_SELECTOR)) return null;
        var next = input.nextElementSibling;
        return next && next.classList && next.classList.contains('color-swatch-wrap') ? next : null;
    }

    // Every wrap whose tooltip is currently shown: hovered wraps plus the wrap
    // owned by the focused swatch radio.
    function shownWraps() {
        var wraps = [];
        function add(wrap) {
            if (wrap && wraps.indexOf(wrap) === -1) wraps.push(wrap);
        }
        Array.prototype.forEach.call(document.querySelectorAll(WRAP_SELECTOR + ':hover'), add);
        add(wrapForInput(document.querySelector(INPUT_SELECTOR + ':focus')));
        return wraps;
    }

    function onKeydown(e) {
        if (e.key !== 'Escape' && e.keyCode !== 27) return;
        shownWraps().forEach(function (wrap) {
            wrap.classList.add(DISMISSED_CLASS);
        });
    }

    // Re-arm a dismissed tooltip only when the pointer ENTERS the wrap again.
    // Using enter (not leave) is deliberate: hiding the tooltip under the pointer
    // can fire a mouseleave that must NOT immediately re-show it. Delegated so it
    // keeps working for swatches re-rendered on variant/section changes.
    function onMouseOver(e) {
        var wrap = e.target.closest ? e.target.closest(WRAP_SELECTOR) : null;
        if (!wrap || !wrap.classList.contains(DISMISSED_CLASS)) return;
        if (e.relatedTarget && wrap.contains(e.relatedTarget)) return; // still inside
        wrap.classList.remove(DISMISSED_CLASS);
    }

    // Re-arm when keyboard focus lands on a swatch radio again. Escape does not
    // move focus, so it fires no focusin and the tooltip stays hidden; only a
    // deliberate return of focus (Tab/arrow back) clears the dismissed state.
    function onFocusIn(e) {
        var wrap = wrapForInput(e.target);
        if (wrap) wrap.classList.remove(DISMISSED_CLASS);
    }

    function init() {
        document.addEventListener('keydown', onKeydown);
        document.addEventListener('mouseover', onMouseOver);
        document.addEventListener('focusin', onFocusIn);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
